/**
 * The account's PCs, as app state keeps them (state.tsx): the order they are
 * listed in and what the hub's `device`, `device_online`, `device_gone` and
 * `dispatch` frames change. Pure, so they are tested rather than read off the
 * screen.
 */

import type { ChatSummary, Device } from '../api';

/** Latest used first, then the PCs never used, latest seen first: the order
 *  `GET /devices` answers in, kept when a frame changes one. */
export function sortDevices(list: readonly Device[]): Device[] {
  return [...list].sort((a, b) => {
    if (a.lastUsedAt !== null && b.lastUsedAt !== null && a.lastUsedAt !== b.lastUsedAt) return b.lastUsedAt - a.lastUsedAt;
    if ((a.lastUsedAt === null) !== (b.lastUsedAt === null)) return a.lastUsedAt === null ? 1 : -1;
    return b.lastSeenAt - a.lastSeenAt || a.id.localeCompare(b.id);
  });
}

/** A PC in place, or added: it was published, renamed, or its tools changed. */
export function putDevice(list: readonly Device[], device: Device): Device[] {
  return sortDevices([device, ...list.filter((d) => d.id !== device.id)]);
}

/** A PC's desktop app opened the hub, or its last socket closed (`at`, in
 *  seconds, is when it was last seen). A PC the list does not hold is left
 *  for the list to find. */
export function withOnline(list: readonly Device[], id: string, online: boolean, at: number): Device[] {
  return sortDevices(list.map((d) => (d.id === id ? { ...d, online, ...(online ? {} : { lastSeenAt: at }) } : d)));
}

/** A PC forgotten. */
export const withoutDevice = (list: readonly Device[], id: string): Device[] => list.filter((d) => d.id !== id);

/** A PC's thread changed: it is the latest used. */
export function withThread(list: readonly Device[], chat: Pick<ChatSummary, 'id' | 'updatedAt' | 'deviceId'>): Device[] {
  if (!chat.deviceId || !list.some((d) => d.id === chat.deviceId)) return [...list];
  return sortDevices(list.map((d) => (d.id === chat.deviceId ? { ...d, chatId: chat.id, lastUsedAt: Math.max(d.lastUsedAt ?? 0, chat.updatedAt) } : d)));
}

/** Words for how long ago a PC was seen, or that it is on now. */
export function seenText(device: Pick<Device, 'online' | 'lastSeenAt'>, now: number): string {
  if (device.online) return 'Online';
  const s = Math.max(0, now - device.lastSeenAt);
  if (s < 90) return 'Seen just now';
  if (s < 3_600) return `Seen ${Math.round(s / 60)} min ago`;
  if (s < 86_400) return `Seen ${Math.round(s / 3_600)} h ago`;
  const days = Math.round(s / 86_400);
  return days === 1 ? 'Seen yesterday' : `Seen ${days} days ago`;
}

/** The glyph for what a PC runs. */
export const platformIcon = (platform: Device['platform']): string =>
  platform === 'windows' ? 'windows-logo' : platform === 'macos' ? 'apple-logo' : 'linux-logo';
